import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthShell } from "@/pages/Login";
import { googleRedirectUri } from "@/components/GoogleAuthButton";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const ROLES = [
  ["MASTER_CLIENT", "Client", "Arrange and supervise care for your family"],
  ["SERVICE_PROVIDER", "Healthcare Provider", "Doctor, nurse or therapist offering home care"],
];

export default function GoogleCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const [pending, setPending] = useState(null);
  const [role, setRole] = useState("MASTER_CLIENT");
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;
    const code = params.get("code");
    const state = params.get("state");
    const expected = sessionStorage.getItem("cn_google_state");
    if (params.get("error") || !code) {
      toast.error("Google sign-in was cancelled");
      navigate("/login", { replace: true });
      return;
    }
    if (!state || state !== expected) {
      toast.error("Google sign-in could not be verified. Please try again.");
      navigate("/login", { replace: true });
      return;
    }
    sessionStorage.removeItem("cn_google_state");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    api
      .post("/api/auth/google/callback", { code, redirect_uri: googleRedirectUri() })
      .then(({ data }) => {
        if (data.needs_role) {
          setPending(data);
          return;
        }
        applySession(data);
        toast.success(`Welcome back, ${data.user.full_name.split(" ")[0]}`);
        navigate(data.next || "/", { replace: true });
      })
      .catch((e) => {
        toast.error(apiError(e));
        navigate("/login", { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const complete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/api/auth/google/complete", {
        signup_token: pending.signup_token,
        role,
        accepted_terms: accepted,
      });
      applySession(data);
      toast.success("Account created — let's complete your verification");
      navigate(data.next || "/", { replace: true });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!pending) {
    return (
      <AuthShell title="Signing you in" subtitle="Confirming your Google account…">
        <div className="flex justify-center py-6" data-testid="google-callback-loading">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-800" />
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="One last step"
      subtitle={`Signed in as ${pending.email}. Tell us how you will use CARENEST.`}
    >
      <div className="space-y-4" data-testid="google-role-picker">
        {ROLES.map(([value, label, hint]) => (
          <label
            key={value}
            className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${
              role === value ? "border-emerald-600 bg-emerald-50" : "border-slate-200 hover:border-emerald-300"
            }`}
          >
            <input
              type="radio"
              name="role"
              value={value}
              checked={role === value}
              onChange={() => setRole(value)}
              className="mt-1 accent-emerald-700"
              data-testid={`google-role-${value.toLowerCase()}`}
            />
            <span>
              <span className="block text-sm font-semibold text-slate-900">{label}</span>
              <span className="block text-xs text-slate-500">{hint}</span>
            </span>
          </label>
        ))}
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-stone-50 p-3">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-0.5 accent-emerald-700"
            data-testid="google-terms-checkbox"
          />
          <span className="text-xs leading-relaxed text-slate-600">
            I agree to the{" "}
            <a href="/terms" target="_blank" rel="noreferrer" className="font-semibold text-emerald-800">Terms & Conditions</a>,{" "}
            <a href="/fees" target="_blank" rel="noreferrer" className="font-semibold text-emerald-800">Fees & settlement</a>{" "}
            and the{" "}
            <a href="/privacy" target="_blank" rel="noreferrer" className="font-semibold text-emerald-800">Privacy & NDPA policy</a>.
          </span>
        </label>
        <Button
          onClick={complete}
          disabled={busy || !accepted}
          className="w-full rounded-full bg-emerald-900 py-6 hover:bg-emerald-800"
          data-testid="google-role-continue-btn"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
        </Button>
        <p className="text-xs text-slate-500">
          We will ask for your phone number and documents during verification.
        </p>
      </div>
    </AuthShell>
  );
}
